import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../cn.js';

/**
 * Card — the default container. Hairline border, no shadow unless it floats.
 *
 * It forwards the rest of its props to the element. That is what lets a caller put
 * `role="status" aria-live="polite"` on a card whose content appears in response to
 * something the person did — without it those attributes are silently dropped, which is
 * exactly what happened to the verification result (T-082).
 */
export function Card({
  children,
  className,
  padded = true,
  as: Comp = 'section',
  ...rest
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
  as?: 'section' | 'div' | 'article';
} & Omit<HTMLAttributes<HTMLElement>, 'className' | 'children'>) {
  return (
    <Comp className={cn('pv-card', padded && 'p-5', className)} {...rest}>
      {children}
    </Comp>
  );
}

export function CardHeader({
  title,
  hint,
  actions,
  description,
  className,
}: {
  title: ReactNode;
  hint?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mb-4 flex items-start gap-3', className)}>
      <div className="min-w-0">
        <h3 className="[font-size:var(--pv-text-h3)] font-bold">{title}</h3>
        {description && (
          <p className="mt-1 [font-size:var(--pv-text-xs)] leading-snug text-[var(--pv-muted)]">
            {description}
          </p>
        )}
      </div>
      {hint && (
        <span className="ml-auto shrink-0 pt-0.5 [font-size:var(--pv-text-xs)] text-[var(--pv-faint)]">
          {hint}
        </span>
      )}
      {actions && <div className={cn('flex shrink-0 items-center gap-2', !hint && 'ml-auto')}>{actions}</div>}
    </div>
  );
}

/**
 * The editorial page header — eyebrow, display title with an optional serif-italic
 * second line, and a lede. Used on dashboards, configuration screens and public pages.
 *
 * Deliberately NOT used above a data grid: a 190k-row table wants a tight operational
 * header, and the two registers must not blur into each other (see tokens.css).
 */
export function PageHeader({
  eyebrow,
  title,
  lede,
  actions,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('flex flex-wrap items-end gap-6', className)}>
      <div className="min-w-0 flex-1">
        {eyebrow && <div className="pv-eyebrow">{eyebrow}</div>}
        <h1 className={cn('pv-display', Boolean(eyebrow) && 'mt-1.5')}>{title}</h1>
        {lede && <p className="pv-lede mt-3">{lede}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  );
}

/**
 * Empty states are designed, not blank (spec §16). A chamber with nothing awaiting
 * action should see that as a finished state, not as a broken screen.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  tone = 'neutral',
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  /** `good` for "you are all caught up", `neutral` for "nothing here yet". */
  tone?: 'neutral' | 'good';
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-[var(--pv-radius-lg)] border border-dashed px-6 py-14 text-center',
        tone === 'good'
          ? 'border-[var(--pv-ok-line)] bg-[var(--pv-ok-bg)]/40'
          : 'border-[var(--pv-line-strong)] bg-[var(--pv-card)]/60',
        className,
      )}
    >
      {icon && (
        <div
          className={cn(
            'mb-4 flex size-11 items-center justify-center rounded-[var(--pv-radius-lg)]',
            tone === 'good'
              ? 'bg-[var(--pv-ok-bg)] text-[var(--pv-ok)]'
              : 'bg-[var(--pv-card-alt)] text-[var(--pv-muted)]',
          )}
        >
          {icon}
        </div>
      )}
      <p className="[font-size:var(--pv-text-h3)] font-bold">{title}</p>
      {description && (
        <p className="mt-2 max-w-[46ch] [font-size:var(--pv-text-sm)] leading-relaxed text-[var(--pv-muted)]">
          {description}
        </p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/**
 * KPI tile. The pastel/tan treatment from spec §16 — but the number is the thing, so
 * the tint is a ground, not a decoration, and the label sits above it in the eyebrow
 * style rather than competing with it.
 */
export function StatTile({
  label,
  value,
  sublabel,
  tone = 'neutral',
  trend,
  onClick,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  sublabel?: ReactNode;
  tone?: 'neutral' | 'good' | 'warn' | 'stop' | 'clay' | 'leaf';
  trend?: { direction: 'up' | 'down' | 'flat'; label: string };
  onClick?: () => void;
  className?: string;
}) {
  const TONES: Record<string, string> = {
    neutral: 'bg-[var(--pv-card)] border-[var(--pv-line)]',
    good: 'bg-[var(--pv-ok-bg)] border-[var(--pv-ok-line)]',
    warn: 'bg-[var(--pv-warn-bg)] border-[var(--pv-warn-line)]',
    stop: 'bg-[var(--pv-stop-bg)] border-[var(--pv-stop-line)]',
    clay: 'bg-[var(--pv-clay-soft)] border-[var(--pv-clay)]',
    leaf: 'bg-[var(--pv-leaf-soft)] border-[var(--pv-leaf)]',
  };
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={cn(
        'flex flex-col rounded-[var(--pv-radius-lg)] border p-4 text-left',
        TONES[tone],
        onClick &&
          'transition-shadow duration-[var(--pv-duration-fast)] hover:shadow-[var(--pv-shadow-sm)]',
        className,
      )}
    >
      {/* No opacity here. `opacity-70` washed this to 3.8:1 against the tile grounds and
          was the last WCAG AA contrast failure in the console (T-082) — at full strength
          the same colour is 8:1. Nothing is lost: the label is already subordinate to the
          number by size and weight, which is where that hierarchy should come from. */}
      <span className="pv-eyebrow text-[var(--pv-ink-soft)]">{label}</span>
      <span className="pv-num mt-2 [font-size:1.75rem] font-bold leading-none tracking-[-0.02em]">
        {value}
      </span>
      {(sublabel || trend) && (
        <span className="mt-2 flex items-center gap-2 [font-size:var(--pv-text-xs)] text-[var(--pv-muted)]">
          {sublabel}
          {trend && (
            <span
              className={cn(
                'font-medium',
                trend.direction === 'up' && 'text-[var(--pv-ok)]',
                trend.direction === 'down' && 'text-[var(--pv-stop)]',
              )}
            >
              {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→'} {trend.label}
            </span>
          )}
        </span>
      )}
    </Comp>
  );
}

/** Inline notice. `stop` blocks, `warn` needs attention, `info` explains. */
export function Callout({
  tone = 'info',
  icon,
  title,
  children,
  actions,
  className,
}: {
  tone?: 'info' | 'warn' | 'stop' | 'ok';
  icon?: ReactNode;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  const TONES = {
    info: 'bg-[var(--pv-info-bg)] border-[var(--pv-info-line)] text-[var(--pv-info)]',
    warn: 'bg-[var(--pv-warn-bg)] border-[var(--pv-warn-line)] text-[var(--pv-warn)]',
    stop: 'bg-[var(--pv-stop-bg)] border-[var(--pv-stop-line)] text-[var(--pv-stop)]',
    ok: 'bg-[var(--pv-ok-bg)] border-[var(--pv-ok-line)] text-[var(--pv-ok)]',
  } as const;
  return (
    <div
      className={cn(
        'grid grid-cols-[auto_1fr] gap-3 rounded-[var(--pv-radius)] border p-3.5 [font-size:var(--pv-text-sm)] leading-relaxed',
        TONES[tone],
        className,
      )}
      role={tone === 'stop' ? 'alert' : undefined}
    >
      <span className="mt-px">{icon}</span>
      <div className="min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn(title && 'mt-1')}>{children}</div>}
        {actions && <div className="mt-3 flex flex-wrap gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('pv-skeleton h-4 w-full', className)} />;
}

/** A labelled key/value row. Used in every detail panel in the product. */
export function KeyValue({
  label,
  children,
  mono,
  className,
}: {
  label: ReactNode;
  children: ReactNode;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-4 border-b border-[var(--pv-line)] py-2 last:border-b-0 last:pb-0',
        className,
      )}
    >
      <dt className="shrink-0 [font-size:var(--pv-text-sm)] text-[var(--pv-muted)]">{label}</dt>
      <dd
        className={cn(
          'm-0 min-w-0 text-right [font-size:var(--pv-text-sm)] font-medium',
          mono && 'pv-mono [font-size:var(--pv-text-xs)]',
        )}
      >
        {children}
      </dd>
    </div>
  );
}
